import axios from 'src/axios';
import { english } from 'src/i18n/language';
import { recordsNote } from '../pedigree-tool/pedigree/deleteConsequences';

export const GENERATE_URL = '/pedigree-ai/generate';
export const APPLY_URL = '/pedigree-ai/apply/:user_id';

const SEX_CODE = { MALE: 'M', FEMALE: 'F' };

// Parent slot wins, then the stated `sex`. Same order as the backend.
function sexByPid(people) {
  const sex = new Map(
    people.map((p) => [p.pid, SEX_CODE[String(p.sex).toUpperCase()] || 'U'])
  );
  for (const person of people) {
    if (person.fatherId != null) sex.set(person.fatherId, 'M');
    if (person.motherId != null) sex.set(person.motherId, 'F');
  }
  return sex;
}

// Founders at 0, everyone else one below their deepest parent. The pass cap
// makes a cyclic config terminate rather than hang.
export function toGenerations(config) {
  const people = config?.people || [];
  const sex = sexByPid(people);
  const depth = new Map(people.map((p) => [p.pid, 0]));

  for (let pass = 0; pass < people.length + 1; pass += 1) {
    let moved = false;
    for (const person of people) {
      const parents = [person.fatherId, person.motherId].filter(
        (p) => p != null && depth.has(p)
      );
      if (!parents.length) continue;
      const want = Math.max(...parents.map((p) => depth.get(p))) + 1;
      if (want > depth.get(person.pid)) {
        depth.set(person.pid, want);
        moved = true;
      }
    }
    if (!moved) break;
  }

  const rows = [];
  for (const person of people) {
    (rows[depth.get(person.pid)] ||= []).push({
      sex: sex.get(person.pid),
      affected: (person.conditions || []).length > 0,
      // So an invented name is visible before it reaches a record.
      name: [person.firstName, person.lastName]
        .filter(Boolean)
        .join(' ')
        .trim(),
      conditions: person.conditions || [],
      deceased: String(person.lifeStatus).toUpperCase() === 'DECEASED',
      label: String(person.pid)
    });
  }

  return rows
    .map((entries, index) => ({ index, people: entries || [] }))
    .filter((g) => g.people.length);
}

// `checked: false` means the lookup never ran, NOT "none of them coded" — which
// would draw every affected relative as unaffected on a transient outage.
export function normaliseConditions(conditions) {
  return {
    coded: Array.isArray(conditions?.coded) ? conditions.coded : [],
    unresolved: Array.isArray(conditions?.unresolved)
      ? conditions.unresolved
      : [],
    checked: conditions ? conditions.checked !== false : false
  };
}

// `null` when the lookup did not run: "unknown", not "none".
export function codedTermSet(conditions) {
  if (!conditions?.checked) return null;
  return new Set((conditions.coded || []).map((c) => c.term));
}

export const CONDITION_STATUS = Object.freeze({
  coded: 'coded',
  other: 'other',
  unchecked: 'unchecked'
});

export function conditionStatus(term, conditions, choices) {
  const choice = choices?.[term];
  if (choice)
    return choice.other ? CONDITION_STATUS.other : CONDITION_STATUS.coded;
  const coded = codedTermSet(conditions);
  if (!coded) return CONDITION_STATUS.unchecked;
  return coded.has(term) ? CONDITION_STATUS.coded : CONDITION_STATUS.other;
}

export function choicesPayload(choices) {
  return Object.entries(choices || {}).map(([term, c]) =>
    c.other ? { term, other: true } : { term, id: c.id, name: c.name }
  );
}

// `config` in the result is what /apply takes; the rest describes it.
export async function generatePedigree({ text, attachments }, options = {}) {
  let data;
  try {
    const res = await axios.post(
      GENERATE_URL,
      { text, ...(attachments?.length ? { attachments } : {}) },
      { signal: options.signal }
    );
    data = res.data;
  } catch (err) {
    // An abort propagates untouched — not a failure worth showing.
    if (err?.name === 'CanceledError' || err?.name === 'AbortError') throw err;
    const message =
      err?.response?.data?.message ||
      'Could not generate the pedigree. Try again.';
    throw new Error(message);
  }

  const config = data?.config;
  if (!config?.people?.length) {
    throw new Error('No people could be read from that description.');
  }

  return {
    config,
    people: config.people.length,
    generations: toGenerations(config),
    conditions: normaliseConditions(data.conditions),
    warnings: data.warnings || [],
    model: data.model
  };
}

// Refused with 409 when the family already has members; `replace` rebuilds it,
// and the error carries `replaceRequired` so the panel can offer that, with
// `existingMembers` and `impact`: what Replace deletes with them, counted by a
// dry run of the delete it runs (null when that count failed). A 409 naming
// `lockedBy` is another user holding the editing lock, which no Replace gets
// past.
export async function applyPedigreeConfig(userId, config, options = {}) {
  const choices = choicesPayload(options.choices);
  try {
    const res = await axios.post(APPLY_URL.replace(':user_id', userId), {
      config,
      ...(options.replace ? { replace: true } : {}),
      ...(choices.length ? { choices } : {})
    });
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const message = data?.message || 'Could not apply the pedigree.';
    const error = new Error(message);
    if (status === 409 && data?.lockedBy) {
      error.lockedBy = data.lockedBy;
    } else if (status === 409) {
      error.replaceRequired = true;
      if (Number.isFinite(data?.existingMembers)) {
        error.existingMembers = data.existingMembers;
        error.impact = data.impact ?? null;
      }
    }
    throw error;
  }
}

// The words for a refused apply, built for the screen with the caller's `t`, or
// null to show the server's own message. The members refusal says what Replace
// deletes, its records in the Delete Person dialog's own line (recordsNote).
export function applyRefusalText(err, t = english) {
  if (err?.lockedBy) {
    return t(
      '{{name}} is editing this pedigree. Apply the sketch once they have finished.',
      { name: err.lockedBy }
    );
  }
  if (!err?.replaceRequired || !Number.isFinite(err.existingMembers)) {
    return null;
  }
  const members =
    err.existingMembers === 1
      ? t(
          'This family already has 1 member. Replace and apply deletes them and builds the family from the sketch.'
        )
      : t(
          'This family already has {{count}} members. Replace and apply deletes them and builds the family from the sketch.',
          { count: err.existingMembers }
        );
  // No count is no reason to say nothing: the delete still takes their records.
  const records = err.impact
    ? recordsNote(err.impact, t)
    : t(
        'Any clinical records they hold (tests, referrals, appointments, pregnancies, care pathways) will also be deleted.'
      );
  return records ? `${members} ${records}` : members;
}
